const pickText = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
};

/** Organiser / customer name for a thread (not the venue owner). */
export const getThreadOrganizerName = (thread) =>
  pickText(
    thread?.organizer_name,
    thread?.organizerName,
    thread?.contact_name,
    thread?.contactName,
    thread?.created_by_name,
    thread?.createdByName
  );

export const getThreadBookingReference = (thread) =>
  pickText(thread?.booking_reference, thread?.bookingReference);

export const getThreadEventName = (thread) =>
  pickText(thread?.event_name, thread?.eventName);

/** Primary line in inbox list — organiser + event (e.g. "Lily Founder breakfast"). */
export const getThreadListTitle = (thread) => {
  const organizer = getThreadOrganizerName(thread);
  const event = getThreadEventName(thread);

  if (organizer && event) {
    return `${organizer} – ${event}`;
  }
  if (organizer) return organizer;
  if (event) return event;

  const reference = getThreadBookingReference(thread);
  if (reference) return reference;

  return pickText(thread?.topic, thread?.venue_name, thread?.venueName, 'Conversation');
};

/** Secondary line — booking reference only when available. */
export const getThreadListSubtitle = (thread) => {
  const reference = getThreadBookingReference(thread);
  if (reference) return reference;

  const event = getThreadEventName(thread);
  if (event) return event;

  const topic = pickText(thread?.topic);
  const genericTopic =
    /^booking enquiry\s*-/i.test(topic) ||
    /^booking\s+[a-z0-9-]+\s*-\s*/i.test(topic);

  if (topic && !genericTopic) return topic;
  return pickText(thread?.venue_name, thread?.venueName, 'Enquiry');
};

export const getThreadAvatarInitial = (thread) => {
  const label = getThreadOrganizerName(thread) || getThreadListTitle(thread);
  return (label || 'E').charAt(0).toUpperCase();
};

/** Header when a thread is open. */
export const getThreadHeaderTitle = (thread) => getThreadListTitle(thread);

export const getThreadHeaderSubtitle = (thread) => getThreadListSubtitle(thread);

/** Postgres user UUID for API calls (not Firebase UID). */
export const getBackendUserId = (currentUser, userProfile) =>
  pickText(userProfile?.backend_user_id, userProfile?.id);

/** @deprecated alias */
export const getVenueMessagingUserId = getBackendUserId;

/** Topic string when creating a new thread from a booking. */
export const buildBookingThreadTopic = ({
  bookingReference,
  eventName,
  organizerName,
  venueName
}) => {
  const ref = pickText(bookingReference);
  const event = pickText(eventName);
  const organizer = pickText(organizerName);
  const venue = pickText(venueName);

  if (ref && organizer) {
    return event ? `${ref} · ${organizer} · ${event}` : `${ref} · ${organizer}`;
  }
  if (ref && event) return `${ref} · ${event}`;
  if (ref) return ref;
  if (organizer && event) return `${organizer} · ${event}`;
  if (event && venue) return `${event} · ${venue}`;
  return venue ? `Booking enquiry · ${venue}` : 'Booking enquiry';
};
